/**
 * Request bodies for `POST /api/v1/marketplace-orders`.
 * Backend copies `productName` / `listingClass` from the product — do not send them on create.
 */

import { normalizeServiceEoiDetails } from "./serviceEoiDetails";

export { normalizeServiceEoiDetails } from "./serviceEoiDetails";

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * @param {unknown} productId
 */
export function coerceMarketplaceProductId(productId) {
  if (productId === null || productId === undefined) {
    throw new Error("productId is required");
  }
  const value = String(productId).trim();
  if (!value) {
    throw new Error("productId is required");
  }
  return value;
}

/**
 * @param {unknown} productId
 */
export function isUuidProductId(productId) {
  return UUID_RE.test(coerceMarketplaceProductId(productId));
}

/**
 * Express interest — `POST /marketplace-orders` with `source: "interest"`.
 * @param {{ productId: string|number, message?: string }} params
 */
export function buildInterestOrderPayload({ productId, message }) {
  const payload = {
    productId: coerceMarketplaceProductId(productId),
    source: "interest",
  };

  const trimmedMessage = typeof message === "string" ? message.trim() : "";
  if (trimmedMessage) {
    payload.message = trimmedMessage;
  }

  return payload;
}

/**
 * Remanufacturing service express of interest with structured `serviceWorkflow.eoiDetails`.
 * @param {{
 *   productId: string|number,
 *   message?: string,
 *   eoiDetails?: Record<string, unknown>|null,
 * }} params
 */
/**
 * @param {{
 *   productId: string|number,
 *   message?: string,
 *   eoiDetails?: Record<string, unknown>|null,
 *   includeServiceWorkflowField?: boolean,
 * }} params
 */
export function buildServiceInterestOrderPayload({
  productId,
  message,
  eoiDetails,
  includeServiceWorkflowField = false,
}) {
  const normalizedEoi = normalizeServiceEoiDetails(eoiDetails);
  const payload = buildInterestOrderPayload({ productId, message });

  if (includeServiceWorkflowField && normalizedEoi) {
    payload.serviceWorkflow = {
      phase: "eoi_received",
      eoiDetails: normalizedEoi,
    };
  }

  return payload;
}

/**
 * Checkout — `POST /marketplace-orders` with `source: "checkout"`.
 * @param {{ productId: string|number, quantity?: number, message?: string }} params
 */
export function buildCheckoutOrderPayload({
  productId,
  quantity = 1,
  message,
}) {
  const payload = {
    productId: coerceMarketplaceProductId(productId),
    source: "checkout",
    quantity: Number.isFinite(Number(quantity)) ? Number(quantity) : 1,
  };

  const trimmedMessage = typeof message === "string" ? message.trim() : "";
  if (trimmedMessage) {
    payload.message = trimmedMessage;
  }

  return payload;
}
